import { useEffect } from 'react';
import { Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import useGeoData from '../../../store/useGeoData'; // Importa lo store per accedere agli ID dei layer

/**
 * Gestisce gli eventi 'mousemove' e 'mouseleave' sulla mappa MapLibre
 * per cambiare il cursore in 'pointer' quando si passa sopra una feature
 * appartenente a un layer gestito.
 * @param map L'istanza della mappa MapLibre.
 */
export function useFeatureHoverCursor(map: MaplibreMap | null) {
    useEffect(() => {
        if (!map) return;

        const handleMouseMove = (e: MapMouseEvent) => {
            const style = map.getStyle();
            if (!style || !style.layers) {
                 map.getCanvas().style.cursor = ''; // Assicurati che il cursore sia resettato
                 return; // Stile non pronto
            }

            // Ottieni gli ID dei layer attualmente presenti nello stile della mappa
            const availableMapLayerIds = new Set(style.layers.map(l => l.id));

            // Filtra gli ID dei layer dallo store per includere solo quelli presenti nello stile
            const queryableLayerIds = useGeoData.getState().dataLayers
                                         .map(l => l.id)
                                         .filter(storeLayerId => availableMapLayerIds.has(storeLayerId));

            // Se non ci sono layer gestiti e visibili su cui interrogare, resetta il cursore ed esci
            if (queryableLayerIds.length === 0) {
                 map.getCanvas().style.cursor = '';
                 return;
            }

            // Interroga le feature nel punto del mouse, SOLO sui layer effettivamente presenti e gestiti
            try {
                const features = map.queryRenderedFeatures(e.point, {
                    layers: queryableLayerIds // Usa solo gli ID filtrati
                });

                // Cambia il cursore se siamo sopra una feature dei nostri layer interrogabili
                if (features.length > 0) {
                    map.getCanvas().style.cursor = 'pointer';
                } else {
                    map.getCanvas().style.cursor = ''; // Cursore predefinito
                }
            } catch (error) {
                // Anche se abbiamo filtrato, potrebbe esserci un errore raro, resettiamo il cursore per sicurezza
                map.getCanvas().style.cursor = '';
            }
        };

        const handleMouseLeave = () => {
            // Ripristina sempre il cursore predefinito quando il mouse esce dalla mappa
            try {
                if (map && map.getCanvas()) { // Verifica aggiuntiva
                    map.getCanvas().style.cursor = '';
                }
            } catch (e) {
            }
        };

        // Aggiungi i listener
        map.on('mousemove', handleMouseMove);
        map.on('mouseleave', handleMouseLeave); // Ascolta sull'intera mappa

        // Funzione di cleanup
        return () => {
            if (map && map.getCanvas()) { // Controlla mappa e canvas
                try {
                    map.off('mousemove', handleMouseMove);
                    map.off('mouseleave', handleMouseLeave);
                    // Ripristina il cursore per sicurezza
                    map.getCanvas().style.cursor = '';
                } catch (e) {
                }
            } else {
            }
        };

    }, [map]); // Dipende solo dalla mappa
}